import * as bd from 'react-basic-design';
import SvgAdd from '../../assets/icons/Add';
import { T, translate } from '../../components/basic/text';
import { SectionTitle } from '../../components/ui/section-title';
import { TProvider } from '../../i18n';

import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useDeleteUserApi, UserDTO, useSaveUserApi, useSearchUserApi } from '../../api/user-api';
import { CellChip } from '../../components/basic/cell-chip';
import { InitFailed } from '../../components/basic/init-failed';
import { Loading } from '../../components/basic/loading';
import { notify } from '../../components/basic/notify';
import { DateCell } from '../../components/table/table-cells';
import { IBasicTableColumn } from '../../interfaces/interfaces';
import { ManageUserModal } from './manage-user-modal';

export const Users = () => {
    const searchUsersApi = useSearchUserApi();
    const [showModal, setShowModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserDTO | undefined>();
    const userInit = useSearchUserApi();
    const saveUser = useSaveUserApi();
    const deleteUser = useDeleteUserApi();
    const { result, error, busy, initialized, initializing } = userInit;

    const columns: IBasicTableColumn<UserDTO>[] = useMemo(
        () => [
            {
                Header: <T>user-name</T>,
                name: 'userName',
                width: 200,
                Cell: ({ row }) => (
                    <bd.Flex align="center" content="start" gap={2}>
                        <span
                            className={classNames({
                                'text-link': searchUsersApi.result?.allowEdit,
                            })}
                            onClick={() => {
                                searchUsersApi.result?.allowEdit && setShowModal(true);
                            }}
                        >
                            {row.userName}
                        </span>
                        {row.isDeleted && <CellChip color="red" label={translate('deleted')} />}
                        {row.isDisabled && <CellChip label={translate('disabled')} />}
                        {row.isServiceUser && (
                            <CellChip color="info" label={translate('service')} />
                        )}
                        {!row.isServiceUser && !row.isDeleted && !row.isDisabled && (
                            <CellChip color="success" label="active" />
                        )}
                    </bd.Flex>
                ),
            },
            {
                Header: <T>display-name</T>,
                name: 'userName',
                Cell: ({ row }) => <div>{`${row.firstName} ${row.lastName}`}</div>,
            },

            {
                Header: <T>phone-number</T>,
                name: 'phoneNumber',
                Cell: ({ row }) => <div>{row.phoneNumber}</div>,
                width: 120,
            },

            {
                Header: <T>last-update</T>,
                name: 'lastUpdate',
                width: 100,
                Cell: ({ row }) => <DateCell value={row.lastUpdate} />,
            },
            {
                Header: <T>created-at</T>,
                name: 'createdAt',
                width: 100,
                Cell: ({ row }) => <DateCell value={row.createdAt} />,
            },
        ],
        [searchUsersApi.result]
    );

    const handleDelete = (user: UserDTO) => {
        bd.msgbox(
            translate('delete-user'),
            translate('confirm-delete-user'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteUser.remove(
                            user,
                            () => {
                                userInit.updateResult({
                                    ...result,
                                    users: result.users.filter(b => b.userName !== user.userName),
                                });
                                notify.dark(translate('selected-user-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedUser(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (user: UserDTO) => {
        saveUser.post(
            null,
            user,
            res => {
                if (selectedUser) {
                    userInit.updateResult({
                        ...result,
                        users: result.users.map(b => (b.userName === user.userName ? res.user : b)),
                    });
                } else {
                    userInit.updateResult({
                        ...result,
                        users: [res.user, ...result.users],
                    });
                }

                setShowModal(false);
                setSelectedUser(undefined);
                notify.success(
                    selectedUser
                        ? translate('selected-user-updated-successfully')
                        : translate('new-user-created-successfully')
                );
            },
            notify.error
        );
    };

    useEffect(() => {
        if (searchUsersApi.initialized || searchUsersApi.initializing) return;
        searchUsersApi.post(null, {});
    }, []);

    if (searchUsersApi.initializing) return <Loading />;
    if (searchUsersApi.error)
        return (
            <InitFailed ex={searchUsersApi.error} onRetry={() => searchUsersApi.post(null, {})} />
        );

    return (
        <TProvider>
            <div className="p-2">
                <SectionTitle
                    title="users-list"
                    hideBorder
                    controls={
                        <bd.Button
                            className="min-w-80"
                            size="sm"
                            variant="outline"
                            disabled={!searchUsersApi.result?.allowEdit}
                            onClick={() => setShowModal(true)}
                        >
                            <SvgAdd />
                            <T>new-user</T>
                        </bd.Button>
                    }
                />

                <bd.BasicTable
                    className="mt-2 mx-n2 mb-n2"
                    columns={columns}
                    data={searchUsersApi.result?.users}
                    cellCSS={({ row }) => (row.isDisabled ? 'bg-gray-5 text-muted opacity-50' : '')}
                    onRowClick={({ row }) => {
                        setSelectedUser(row);
                        setShowModal(true);
                    }}
                    hover
                />

                <ManageUserModal
                    show={showModal}
                    onHide={() => {
                        setShowModal(false);
                        setSelectedUser(undefined);
                    }}
                    onSave={handleSave}
                    onDeleted={() => selectedUser && handleDelete(selectedUser)}
                    allowEdit={result?.allowEdit}
                    user={selectedUser}
                />
            </div>
        </TProvider>
    );
};
